/**
 * Per-node-type mapping between a React Flow handle id (a string like
 * `'true'`/`'false'`, `'in'`/`'out'`, `'input'`/`'output_2'` — or `undefined`
 * for a node type whose single handle carries no explicit id) and the small
 * numeric "pin index" the backend persists on a connection
 * (`graph_connections.source_pin`/`target_pin`, doc 03 §2).
 *
 * **Why this exists:** `source_pin`/`target_pin` were designed as plain
 * ordinal pin indices, but the node components that actually exist use
 * heterogeneous string handle ids (see each node type's own `<Handle id=…>`).
 * `projectStore.ts` was never translating between the two — edges lost their
 * handle identity on every reload (Condition's true/false branch, in
 * particular, silently collapsed to whichever branch pin 0 happened to be).
 * This registry is the single place that encodes each node type's handle
 * list, in pin order, so the store can convert in both directions without
 * hardcoding node-type knowledge at the save/load call sites.
 *
 * Node types not listed here have exactly one implicit handle per side (no
 * `id` prop on their `<Handle>` — fragment, hub, tilemap_ref, image, comment,
 * group, note): pin 0, handle `undefined`, on both sides.
 */

type HandleList = string[] | ((data: Record<string, unknown>) => string[]);

interface NodePinSpec {
    /** Output (source) handle ids, in pin order. */
    sourceHandles?: HandleList;
    /** Input (target) handle ids, in pin order. */
    targetHandles?: HandleList;
}

const pinSpecs: Record<string, NodePinSpec> = {
    // entry.tsx: single source handle id="out" (no target — it's a graph's start marker).
    entry: { sourceHandles: ['out'] },
    // exit.tsx: single target handle id="in" (no source — it's a graph's end marker).
    exit: { targetHandles: ['in'] },
    // instruction.tsx: id="in" / id="out".
    instruction: { sourceHandles: ['out'], targetHandles: ['in'] },
    // condition.tsx: target id="in"; two source handles, id="true" (pin 0) / id="false" (pin 1).
    condition: { sourceHandles: ['true', 'false'], targetHandles: ['in'] },
    // container.tsx: target id="input"; one source handle per output pin,
    // id={`output_${i}`} — count is dynamic (`data.outputs`, default 1).
    container: {
        targetHandles: ['input'],
        sourceHandles: (data) => {
            const count = typeof data.outputs === 'number' ? data.outputs : 1;
            return Array.from({ length: Math.max(1, count) }, (_, i) => `output_${i}`);
        },
    },
};

function resolveHandles(list: HandleList | undefined, data: Record<string, unknown>): string[] {
    if (!list) return [];
    return typeof list === 'function' ? list(data) : list;
}

/** Handle id (possibly `null`/`undefined`, React Flow's default) → pin index for the SOURCE side. */
export function pinForSourceHandle(nodeType: string | undefined, data: Record<string, unknown>, handle: string | null | undefined): number {
    const handles = resolveHandles(pinSpecs[nodeType ?? '']?.sourceHandles, data);
    if (handles.length === 0) return 0;
    const idx = handles.indexOf(handle ?? '');
    return idx >= 0 ? idx : 0;
}

/** Pin index → handle id for the SOURCE side (`undefined` = the node's single implicit handle). */
export function sourceHandleForPin(nodeType: string | undefined, data: Record<string, unknown>, pin: number): string | undefined {
    const handles = resolveHandles(pinSpecs[nodeType ?? '']?.sourceHandles, data);
    return handles[pin];
}

/** Handle id → pin index for the TARGET side. */
export function pinForTargetHandle(nodeType: string | undefined, data: Record<string, unknown>, handle: string | null | undefined): number {
    const handles = resolveHandles(pinSpecs[nodeType ?? '']?.targetHandles, data);
    if (handles.length === 0) return 0;
    const idx = handles.indexOf(handle ?? '');
    return idx >= 0 ? idx : 0;
}

/** Pin index → handle id for the TARGET side (`undefined` = the node's single implicit handle). */
export function targetHandleForPin(nodeType: string | undefined, data: Record<string, unknown>, pin: number): string | undefined {
    const handles = resolveHandles(pinSpecs[nodeType ?? '']?.targetHandles, data);
    return handles[pin];
}
